import { ILocation } from 'src/interfaces';

/**
 * Build PATCH payloads only for locations whose tag membership changed vs persisted (current page).
 *
 * Removed locations get the tag id stripped from `location_tag_id`; newly selected locations get
 * the tag id appended (idempotent). Unchanged rows are skipped to avoid unnecessary writes.
 */
export const buildLocationTagAssignmentUpdates = (
  currentSelected: ILocation[],
  persistedSelected: ILocation[] | undefined,
  tagId: number,
  locations: ILocation[],
  delta: Map<number | string, number>,
): ILocation[] => {
  const persisted = persistedSelected ?? [];
  const initialKeys = new Set(persisted.map((loc) => loc.location_code));
  const currentKeys = new Set(currentSelected.map((loc) => loc.location_code));

  const removedKeys = [...initialKeys].filter((k) => !currentKeys.has(k));
  const addedKeys = [...currentKeys].filter((k) => !initialKeys.has(k));

  const updates: ILocation[] = [];

  for (const key of removedKeys) {
    const entity = persisted.find((loc) => loc.location_code === key);
    if (!entity) continue;
    const nextTags =
      entity.location_tag_id?.filter((tid) => tid !== tagId) ?? [];
    updates.push({ ...entity, location_tag_id: nextTags });
  }

  for (const key of addedKeys) {
    const entity = currentSelected.find((loc) => loc.location_code === key);
    if (!entity) continue;
    const tags = entity.location_tag_id ?? [];
    updates.push({
      ...entity,
      location_tag_id: tags.includes(tagId) ? tags : [...tags, tagId],
    });
  }

  for (const [locCode, deltaVal] of delta) {
    if (deltaVal > 0) {
      if (!updates.find((loc) => loc.location_code === locCode)) {
        const foundLoc = locations.find((l) => l.location_code === locCode);
        if (foundLoc) {
          updates.push(foundLoc);
        }
      }
    }
  }

  return updates;
};
